import { isEqual } from "lodash";

import makeObservable from "./makeObservable";

const DEFAULT_SEARCH_RESULT_LIMIT = 50;

const INITIAL_SEARCH_RESULT_NAVIGATION_STATE = {
  filterKeys: [],
  queryOptions: {},
  count: 0,
  limit: DEFAULT_SEARCH_RESULT_LIMIT,
  listContext: {},
  offset: 0,
  pageIds: [],
  searchSynopsisTerms: [],
  statusContext: "all",
};

const toNumber = (value, fallback = 0) => {
  const parsedValue = Number(value);
  return Number.isFinite(parsedValue) ? parsedValue : fallback;
};

const getPageIds = (edges = []) =>
  edges
    .map(edge => {
      const rawId = edge?.id;
      if (rawId === null || rawId === undefined || rawId === "") {
        return NaN;
      }
      return toNumber(rawId, NaN);
    })
    .filter(Number.isFinite);

const buildSearchResultNavigationSnapshot = ({
  count = 0,
  edges = [],
  listContext = {},
  queryVariables = {},
  filters = {},
  searchSynopsisTerms,
  statusContext = "all",
} = {}) => {
  const limit =
    toNumber(queryVariables?.limit, DEFAULT_SEARCH_RESULT_LIMIT) ||
    DEFAULT_SEARCH_RESULT_LIMIT;
  const offset = toNumber(queryVariables?.offset, 0);
  const queryEntries = Object.entries(queryVariables).filter(
    ([key]) => !["limit", "offset"].includes(key),
  );
  // Remember only which canonical filters produced these results. Values
  // added or overridden by the list (including URL searches) stay query-only.
  const filterKeys = queryEntries
    .filter(([key, value]) => key in filters && isEqual(value, filters[key]))
    .map(([key]) => key);
  const queryOptions = Object.fromEntries(
    queryEntries.filter(([key]) => !filterKeys.includes(key)),
  );

  return {
    filterKeys,
    queryOptions,
    count: toNumber(count, 0),
    limit,
    listContext,
    offset,
    pageIds: getPageIds(edges),
    // A missing list uses a fresh array. The shared initial array must not
    // collect terms that would then appear in every module.
    searchSynopsisTerms: searchSynopsisTerms ?? [],
    statusContext,
  };
};

const getSearchResultNavigationState = ({ recordId, snapshot } = {}) => {
  const emptyState = {
    hasNext: false,
    hasPrevious: false,
    nextId: null,
    nextOffset: null,
    previousId: null,
    previousOffset: null,
  };

  if (!snapshot || typeof recordId !== "number") {
    return emptyState;
  }

  const {
    count = 0,
    limit = DEFAULT_SEARCH_RESULT_LIMIT,
    offset = 0,
    pageIds = [],
  } = snapshot;
  const indexOnPage = pageIds.findIndex(id => id === recordId);

  if (indexOnPage < 0) {
    return emptyState;
  }

  const globalIndex = offset + indexOnPage;
  const hasPrevious = globalIndex > 0;
  const hasNext = globalIndex < count - 1;
  const previousId = indexOnPage > 0 ? pageIds[indexOnPage - 1] : null;
  const nextId =
    indexOnPage < pageIds.length - 1 ? pageIds[indexOnPage + 1] : null;

  return {
    hasNext,
    hasPrevious,
    nextId,
    nextOffset: hasNext && !nextId ? offset + limit : null,
    previousId,
    previousOffset:
      hasPrevious && !previousId ? Math.max(offset - limit, 0) : null,
  };
};

// Mutable navigation metadata must not be shared between modules.
const copySearchResultNavigationState = (
  state = INITIAL_SEARCH_RESULT_NAVIGATION_STATE,
) => ({
  ...state,
  filterKeys: [...(state.filterKeys || [])],
  queryOptions: { ...(state.queryOptions || {}) },
  listContext: { ...(state.listContext || {}) },
  pageIds: [...(state.pageIds || [])],
  searchSynopsisTerms: [...(state.searchSynopsisTerms || [])],
});

const createSearchResultNavigationStore = (
  filterStore = makeObservable({}),
) => {
  const store = makeObservable(copySearchResultNavigationState());
  // Form effects can publish equivalent filters repeatedly. Only an actual
  // criteria change invalidates the result IDs and their query context.
  let previousFilters = filterStore.get();
  filterStore.subscribe(filters => {
    const changed = !isEqual(previousFilters, filters);
    previousFilters = filters;
    if (changed) store.set(copySearchResultNavigationState());
  });
  return { ...store, filterStore };
};

const getSearchResultNavigationQuery = (store, pageOffset) => {
  const snapshot = store.get();
  const filters = store.filterStore.get();
  return {
    ...Object.fromEntries(snapshot.filterKeys.map(key => [key, filters[key]])),
    ...snapshot.queryOptions,
    limit: snapshot.limit || DEFAULT_SEARCH_RESULT_LIMIT,
    offset: pageOffset,
  };
};

const hasSearchResultNavigationContext = ({ snapshot, status } = {}) =>
  snapshot?.statusContext === status && (snapshot?.pageIds || []).length > 0;

// Results tables report after every render, and the list page subscribes to
// the store, so an unchanged snapshot must not be written again.
const updateSearchResultNavigationStore = (store, values) => {
  const snapshot = buildSearchResultNavigationSnapshot({
    ...values,
    filters: store.filterStore.get(),
  });
  if (!isEqual(store.get(), snapshot)) store.set(snapshot);
};

export {
  DEFAULT_SEARCH_RESULT_LIMIT,
  INITIAL_SEARCH_RESULT_NAVIGATION_STATE,
  buildSearchResultNavigationSnapshot,
  createSearchResultNavigationStore,
  getSearchResultNavigationState,
  getSearchResultNavigationQuery,
  hasSearchResultNavigationContext,
  updateSearchResultNavigationStore,
};
